import { ReactNode, useEffect, useRef, useState } from "react";
import { CheckIcon, PlusIcon, SearchIcon, XIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { cn } from "@/src/lib/utils";
import { useDebounce } from "@/src/lib/useDebounce";
import { useResizeObserver } from "@/src/lib/useResizeObserver";
import { Separator } from "./separator";

export type ComboboxYachtsSearchProps<T> = {
  onAddOption?: () => Promise<T | null>;
  className?: string;
  renderOption?: (value: T) => string;
  loadOptions: (query: string) => Promise<T[]>;
  onChange?: (value: T | null) => void;
  value?: T[] | null;
  placeholder?: string;
  optionLabel?: (value: T) => string;
  // Shown in the dropdown when there's no query yet (e.g. popular-search shortcuts)
  // instead of "No results", so it isn't overlaid by this combobox's own popover.
  emptyContent?: ReactNode;
};

export function ComboboxYachtsSearch<T>(props: ComboboxYachtsSearchProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<T[]>([]);
  const [width, setWidth] = useState(0);

  const renderValue = (value: T | T[]) => {
    if (Array.isArray(value)) {
      return value.map((v) => (props.renderOption ? props.renderOption(v) : String(v))).join(", ");
    }
    return props.renderOption ? props.renderOption(value) : String(value);
  };
  const search = useDebounce(async (v: string) => {
    try {
      const results = await props.loadOptions(v);
      setItems(results || []);
    } catch (error) {
      console.error("Error loading options:", error);
      setItems([]);
    }
  }, 300);

  const onQueryChange = (v: string) => {
    setQuery(v);
    search(v);
  };

  useEffect(() => {
    setQuery("");
    setItems([]);
  }, [props.value]);

  const triggerRef = useResizeObserver(([e]) => {
    setWidth(e.target.getBoundingClientRect().width);
  });

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setOpen(true);
  }, []);

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
      }}
    >
      <div className="text-slate-300 flex gap-x-2 overflow-x-auto w-[400px]">
        {props.value
          ? props.value?.map((value: any) => {
              return (
                <div
                  className="text-slate-700 flex flex-row bg-blue-50 rounded-xl my-2 p-2 justify-between items-center text-xs cursor-pointer hover:bg-red-300 min-w-[max-content] max-w-[140px] "
                  onClick={() => {
                    props.onChange?.(value);
                    setQuery("");
                  }}
                >
                  <div className="flex flex-row gap-x-2">
                    <span className="line-clamp-1 break-all" title={renderValue(value)}>
                      {renderValue(value)}
                    </span>
                    <span className="text-[10px] text-slate-500 flex flex-row justify-between items-center ">
                      {value.entityType}
                    </span>
                  </div>
                </div>
              );
            })
          : null}
      </div>
      <PopoverTrigger className={props.className} asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-[200px] justify-between bg-white hover:bg-white", props.className)}
        >
          <span className="line-clamp-1">{props.placeholder}</span>
          <SearchIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-[200px] p-0", props.className)} style={{ width }}>
        <div className="flex w-full flex-col rounded-md bg-popover text-popover-foreground">
          <div className="flex items-center px-3 border-b">
            <input
              ref={inputRef}
              className="flex w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 h-9"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              role="combobox"
              placeholder={props.placeholder}
            />
            <SearchIcon className="h-4 w-4 shrink-0 opacity-50" />
          </div>
          {items.length === 0 &&
            (props.emptyContent && query.trim() === "" ? (
              <div className="p-3">{props.emptyContent}</div>
            ) : (
              <div className="px-3 py-8 text-center text-sm text-popover-foreground">No results</div>
            ))}
          {items.length > 0 && (
            <div
              className="max-h-[300px] p-1 overflow-y-auto overflow-x-hidden"
              role="listbox"
              aria-label="Suggestions"
            >
              {items.map((item, i) => (
                <div
                  key={i}
                  className={`relative flex justify-between cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground ${
                    props.value && renderValue(props.value) === renderValue(item) ? "bg-slate-100" : ""
                  }`}
                  role="option"
                  onClick={() => {
                    props.onChange?.(props.value === item ? null : item);
                    setOpen(false);
                  }}
                >
                  {renderValue(item)}
                  {props.optionLabel && (
                    <span className="ml-auto text-xs border rounded-sm py-1 px-1 bg-slate-100">
                      {props.optionLabel(item)}
                    </span>
                  )}
                  {props.value && renderValue(props.value) === renderValue(item) && (
                    <CheckIcon className="ml-2" size={16} />
                  )}
                </div>
              ))}
              {props.onAddOption && (
                <>
                  <Separator />
                  <Button
                    onClick={() => {
                      props.onAddOption?.().then((t) => {
                        props.onChange?.(t);
                        if (t) {
                          onQueryChange(renderValue(t));
                        }
                      });
                    }}
                    className="w-full"
                    variant="ghost"
                    size="sm"
                  >
                    Add new <PlusIcon size={16} />
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
